package ai.rules

import i18n.StringKey
import i18n.tr
import kotlin.math.roundToInt

/** Which column of the diagram a band lands in. */
enum class SankeyLevel { INCOME, BUCKET, PRIMARY, SUB }

/** Horizontal alignment of a piece of text against its [SankeyText.x]. */
enum class SankeyTextAnchor { START, MIDDLE }

data class SankeyPoint(val x: Double, val y: Double)

/** A cubic segment: [start] → [end] with two control points, ready for
 *  SVG `C`, SwiftUI `Path.addCurve`, or Compose `Path.cubicTo`. */
data class SankeyCurve(
    val start: SankeyPoint,
    val control1: SankeyPoint,
    val control2: SankeyPoint,
    val end: SankeyPoint,
)

/**
 * The ribbon flowing into a band's node, at full thickness.
 *
 * Stored as primitives (so a renderer can walk it however it likes) and
 * exposed as the two cubic edges every renderer actually draws: follow
 * [topEdge], line down to [bottomEdge]'s start, follow [bottomEdge], close.
 */
data class SankeyRibbon(
    val sourceX: Double,
    val sourceTopY: Double,
    val targetX: Double,
    val targetTopY: Double,
    /** Vertical thickness; identical at both ends — flow is conserved. */
    val thickness: Double,
    /** x of both control points: the midpoint between source and target. */
    val controlX: Double,
    val colorHex: String,
) {
    val topEdge: SankeyCurve
        get() = SankeyCurve(
            SankeyPoint(sourceX, sourceTopY),
            SankeyPoint(controlX, sourceTopY),
            SankeyPoint(controlX, targetTopY),
            SankeyPoint(targetX, targetTopY),
        )

    val bottomEdge: SankeyCurve
        get() = SankeyCurve(
            SankeyPoint(targetX, targetTopY + thickness),
            SankeyPoint(controlX, targetTopY + thickness),
            SankeyPoint(controlX, sourceTopY + thickness),
            SankeyPoint(sourceX, sourceTopY + thickness),
        )
}

/** The rounded rectangle a band terminates in. */
data class SankeyNode(
    val x: Double,
    val y: Double,
    val width: Double,
    val height: Double,
    val cornerRadius: Double,
    val colorHex: String,
    /** 1.0 for income and buckets; deeper levels fade slightly. */
    val opacity: Double,
)

/** A run of text, positioned at its [baselineY] (not its box top). */
data class SankeyText(
    val text: String,
    val x: Double,
    val baselineY: Double,
    val fontSize: Double,
    val colorHex: String,
    val anchor: SankeyTextAnchor,
)

/**
 * One hoverable strand: the ribbon that carries the money, the node it lands
 * in, and the inline label if the band is thick enough to hold one.
 *
 * Carries its own tooltip payload so a renderer never has to re-derive it.
 */
data class SankeyBand(
    val level: SankeyLevel,
    /** Raw code — `BudgetBucket.name`, a Plaid PRIMARY/DETAILED code, or one of
     *  [SankeyGeometry.CODE_INCOME] / [SankeyGeometry.CODE_OTHER] /
     *  [SankeyGeometry.CODE_REMAINING]. Stable across locales. */
    val categoryCode: String,
    /** Localized, user-facing name. */
    val label: String,
    val amount: Double,
    /** [amount] pre-formatted as money, e.g. `$1,800`. */
    val amountText: String,
    val parentLabel: String?,
    /** The parent's raw code — a `BudgetBucket.name` for a primary, a primary
     *  code for a leaf, [SankeyGeometry.CODE_INCOME] for a bucket. Stable across
     *  locales, unlike [parentLabel], so it is the only one of the two a renderer
     *  may use as an identity key (see [SankeyFocus.parentBucket]). */
    val parentCode: String? = null,
    val parentAmount: Double?,
    /** Whole-percent share of [parentAmount]; null for the income root. */
    val percentOfParent: Int?,
    val colorHex: String,
    /** Stable hook for platform interactivity (web element id, mobile hit tag). */
    val interactiveId: String?,
    /** null only for the income root, which nothing flows into. */
    val ribbon: SankeyRibbon?,
    val node: SankeyNode,
    val text: SankeyText?,
)

/**
 * The band a drilled-in layout is re-rooted on — the progressive-disclosure
 * answer to a narrow widget. A portrait phone only fits three columns, so the
 * leaves never appear; tapping a band and re-laying out on that subtree spends
 * the whole width on its children instead of rotating the device.
 *
 * Built straight from a tapped [SankeyBand]:
 * `SankeyFocus(band.level, band.categoryCode, band.parentCode)`.
 */
data class SankeyFocus(
    val level: SankeyLevel,
    val categoryCode: String,
    /**
     * The bucket the focused band hangs under, as a `BudgetBucket.name` — what
     * makes a PRIMARY focus unambiguous. The same primary legitimately appears
     * under more than one bucket once the user re-buckets its sub-categories
     * (Food & Drink part Need, part Want), and [categoryCode] alone cannot tell
     * the two copies apart, so the later one could never be focused.
     *
     * Null means "whichever bucket holds it first" — the historical behaviour,
     * kept so a caller that has no parent to hand still resolves. Ignored at the
     * other levels: bucket codes are already unique, income is the root, and a
     * leaf has nothing to drill into.
     */
    val parentBucket: String? = null,
)

/**
 * A fully resolved Sankey, in pixels, with nothing platform-specific in it —
 * no SVG, no CSS, no DOM. Draw [bands] in order: each band paints its ribbon,
 * then its node, then its label, so later ribbons wash over earlier labels
 * exactly as the web has always done.
 */
data class SankeyScene(
    val width: Double,
    val height: Double,
    val isDark: Boolean,
    /** Levels actually drawn: 2 = income→buckets, 3 = +primaries, 4 = +subs.
     *  In a focused scene it counts the columns of the drilled-in subtree. */
    val levels: Int,
    val bands: List<SankeyBand>,
    /** Set instead of [bands] when there is nothing to draw. */
    val emptyText: SankeyText?,
    /** The focus this scene was actually laid out for — null for the full
     *  diagram, and also null when the requested focus had nothing to reveal
     *  (focusing is a no-op then, never an empty or degenerate diagram). */
    val focus: SankeyFocus? = null,
    /** Localized name of the focused root: the breadcrumb / back-button title. */
    val focusLabel: String? = null,
    /** The focused root's total. Every [SankeyBand.percentOfParent] in a focused
     *  scene is a share of this, not of income. */
    val focusAmount: Double? = null,
    /** [focusAmount] pre-formatted as money, e.g. `$1,800`. */
    val focusAmountText: String? = null,
) {
    val isEmpty: Boolean get() = bands.isEmpty()

    /** True when the renderer must offer a way back to the full diagram. */
    val isFocused: Boolean get() = focus != null
}

/**
 * Lays out the Needs & Wants Flow Sankey. Platform-neutral: every renderer
 * (web SVG, SwiftUI Path, Compose Canvas) consumes the same [SankeyScene], so
 * the geometry lives here once instead of once per platform.
 *
 * Income enters on the left; buckets, then primaries, then sub-categories fan
 * out to the right. It is a real Sankey, not a stacked bar: a node's outgoing
 * ribbons stack **flush** at the source so they sum to exactly the node's
 * height (conservation of flow), and only fan apart to the gapped destination
 * nodes — that separation at the destinations is what reads as a flow.
 *
 * A single value→pixel scale is shared by every level, sized so the busiest
 * column (the one with the most nodes, hence the most inter-node gaps) still
 * fits the height. Shorter columns simply carry empty space.
 *
 * A width that only fits three columns hides the leaves entirely; pass a
 * [SankeyFocus] to [layout] and the diagram is re-rooted on that band, spending
 * the whole width on its subtree instead of asking the user to rotate.
 */
object SankeyGeometry {

    const val NODE_W = 14
    const val PAD = 12

    /** Space between a node's right edge and the start of its label. */
    const val LABEL_PAD = 6

    /** Below this thickness a band gets no inline label — its tooltip covers it on hover. */
    const val LABEL_MIN_PX = 13

    /** Leaf (sub-category) labels use a smaller font, so they stay legible on a
     *  thinner band — a 9%-of-parent leaf like a credit-card payment should still
     *  get its name, not just a hover tooltip. */
    const val LEAF_LABEL_MIN_PX = 10

    /** Thinnest a node bar is ever drawn, however small its share.
     *
     *  Purely proportional sizing gives a 1%-of-parent leaf a sub-pixel bar: it
     *  merges into its neighbour, falls under [LABEL_MIN_PX] so it loses its
     *  name, and sits inside the hit padding its neighbours add — which is how a
     *  tap on one category came back naming the next one down.
     *
     *  A floor of [NODE_W] makes the stub at least as tall as it is wide, which
     *  clears [LABEL_MIN_PX] and so guarantees every drawn node is both labelled
     *  and separately touchable.
     *
     *  Only the NODE is floored. Its ribbon keeps the true proportional
     *  thickness and meets the stub at the stub's centre, so the floor buys
     *  reachability without ever making a trickle look like a torrent. */
    const val MIN_NODE_PX = NODE_W

    /** Resting / hovered ribbon opacity, so every platform fades them alike. */
    const val RIBBON_OPACITY = 0.32
    const val RIBBON_HOVER_OPACITY = 0.6

    const val CODE_INCOME = "__INCOME__"
    const val CODE_OTHER = "__OTHER__"
    const val CODE_REMAINING = "__REMAINING__"

    /** Gap between sibling nodes sharing a parent, and the larger gap between the
     *  top-level buckets. Scaled to height so they read on a tall widget without
     *  swallowing a short one. */
    private fun gapFor(plotH: Int): Int = (plotH / 55).coerceIn(3, 10)
    private fun bucketGapFor(plotH: Int): Int = (plotH / 38).coerceIn(5, 15)

    private const val CHAR_WIDTH_EM = 0.6

    private fun textWidth(text: String, fontSize: Int): Double = text.length * fontSize * CHAR_WIDTH_EM

    /** Room a label at [nodeX] has before the right padding. */
    private fun labelBudget(nodeX: Int, widthPx: Int): Double =
        (widthPx - PAD - (nodeX + NODE_W + LABEL_PAD)).toDouble()

    /** [text] clipped with an ellipsis until it fits [maxWidthPx]; "" if nothing fits. */
    private fun fitLabel(text: String, fontSize: Int, maxWidthPx: Double): String {
        if (textWidth(text, fontSize) <= maxWidthPx) return text
        val chars = (maxWidthPx / (fontSize * CHAR_WIDTH_EM)).toInt() - 1
        if (chars < 1) return ""
        return text.take(chars).trimEnd() + "…"
    }

    private fun bucketColor(b: BudgetBucket, isDark: Boolean): String = when (b) {
        BudgetBucket.NEEDS -> "#3B82F6"
        BudgetBucket.WANTS -> "#F59E0B"
        BudgetBucket.SAVINGS -> "#10B981"
        BudgetBucket.UNCLASSIFIED -> if (isDark) "#6B7280" else "#9CA3AF"
        BudgetBucket.IGNORE -> "#374151" // never rendered; kept for exhaustiveness
    }

    private fun money(v: Double): String {
        val grouped = kotlin.math.round(kotlin.math.abs(v)).toLong().toString()
            .reversed().chunked(3).joinToString(",").reversed()
        return (if (v < 0) "-$" else "$") + grouped
    }

    /** One category node once we've decided what to show (rest folded into "Other"). */
    private class ShownCat(val label: String, val amount: Double, val children: List<FlowNode>)

    private fun shownOf(nodes: List<FlowNode>, perParent: Int): List<ShownCat> {
        val shown = nodes.take(perParent).map { ShownCat(it.label, it.amount, it.children) }
        val rest = nodes.drop(perParent).sumOf { it.amount }
        return if (rest > 0.0) shown + ShownCat(CODE_OTHER, rest, emptyList()) else shown
    }

    /** An L1 strand: a bucket, or the merged/leftover tail (primaries == null). */
    private class Strand(
        val code: String,
        val label: String,
        val amount: Double,
        val color: String,
        val interactiveId: String?,
        val primaries: List<ShownCat>?,
    )

    /** A drilled primary, recorded during the L2 pass so its subs can be a centred L3 column. */
    private class Drilled(
        /** Raw code, so its children can strip it as their prefix. */
        val code: String,
        /** Localized name, already resolved for the L2 column. */
        val label: String,
        val color: String,
        val srcY: Double,
        val amount: Double,
        val subs: List<ShownCat>,
    )

    /**
     * Everything [paint] needs: a root node and the strands leaving it, with each
     * column's [SankeyLevel] already decided. The full diagram and the focused
     * ones are the same geometry re-rooted one or two levels deeper, so they run
     * through a single painter instead of a second, drifting copy of it.
     */
    private class Plan(
        /** Columns to draw, ≥ 2. Mirrors [SankeyScene.levels]. */
        val levels: Int,
        /** Level carried by each column, root first; always 4 long. */
        val columnLevels: List<SankeyLevel>,
        val rootCode: String,
        val rootLabel: String,
        val rootAmount: Double,
        val rootColor: String,
        val strands: List<Strand>,
    )

    private fun ribbon(x1: Int, y1: Double, x2: Int, y2: Double, h: Double, color: String) =
        SankeyRibbon(
            sourceX = x1.toDouble(),
            sourceTopY = y1,
            targetX = x2.toDouble(),
            targetTopY = y2,
            thickness = h,
            controlX = (x1 + x2) / 2.0,
            colorHex = color,
        )

    /** One column's worth of drawn nodes for the scale fitter: the amounts that
     *  set their thicknesses, plus the gap pixels fixed between them. */
    private class Column(val values: List<Double>, val gapPx: Double)

    /** An L1 strand after placement. Once the floor bites, the bar drawn and the
     *  flow it carries are no longer the same height, and the children need
     *  both: the bar to sit against, the flow to stack flush within. */
    private class Placed(
        val strand: Strand,
        val y: Double,
        val nodeH: Double,
        val thickness: Double,
    )

    /** A node's drawn height: its true share, never thinner than [floor]. */
    private fun nodeH(amount: Double, scale: Double, floor: Double): Double =
        maxOf(amount * scale, floor)

    /** Height [c] occupies at [scale]. Non-decreasing in [scale] — the property
     *  [fitScale]'s bisection stands on. */
    private fun columnH(c: Column, scale: Double, floor: Double): Double =
        c.values.sumOf { nodeH(it, scale, floor) } + c.gapPx

    /**
     * The tallest floor every column can seat, never above [MIN_NODE_PX].
     *
     * A column holding more nodes than the plot can seat at the full floor would
     * overflow at any scale, so it caps the floor at an even division instead.
     * Such a column can't give every node a label-sized bar, but an evenly
     * shared floor still beats the sub-pixel slivers proportional sizing leaves.
     */
    private fun floorFor(cols: List<Column>, plotH: Double): Double {
        var f = MIN_NODE_PX.toDouble()
        for (c in cols) {
            if (c.values.isEmpty()) continue
            f = minOf(f, (plotH - c.gapPx).coerceAtLeast(0.0) / c.values.size)
        }
        return f.coerceAtLeast(0.0)
    }

    /**
     * The largest value→px scale at which every column still fits [plotH] once
     * [floor] is applied.
     *
     * The floor costs the closed form the unfloored layout used: lifting a node
     * to the floor spends pixels its share never earned, and *which* nodes need
     * lifting depends on the scale being solved for. [columnH] is monotonic in
     * scale, though, so a bisection converges without tracking that set — and
     * scale 0 always fits, because [floorFor] already sized the floor to.
     */
    private fun fitScale(cols: List<Column>, plotH: Double, floor: Double): Double {
        val drawn = cols.filter { it.values.isNotEmpty() }
        if (drawn.isEmpty()) return 1.0
        // Purely proportional: no column can want a larger scale than this.
        var hi = drawn.minOf { c ->
            val sum = c.values.sum()
            if (sum > 0.0) (plotH - c.gapPx).coerceAtLeast(1.0) / sum else Double.MAX_VALUE
        }
        if (hi == Double.MAX_VALUE) return 1.0
        fun fits(s: Double) = drawn.all { columnH(it, s, floor) <= plotH }
        if (fits(hi)) return hi
        var lo = 0.0
        repeat(48) {
            val mid = (lo + hi) / 2
            if (fits(mid)) lo = mid else hi = mid
        }
        return lo
    }

    /**
     * Localized label for a Plaid PRIMARY code, via CategoryUtils (the single
     * source of truth). `formatCategoryNameLocalized` (not
     * `getEffectiveDisplayCategory`) is right here: this strand aggregates the
     * whole primary, so RENT_AND_UTILITIES reads "Rent & Utilities".
     */
    private fun primaryLabel(code: String): String =
        if (code == CODE_OTHER) StringKey.CATEGORY_OTHER.tr()
        else utils.CategoryUtils.formatCategoryNameLocalized(code)

    /** Localized leaf label for a DETAILED code shown next to its primary. */
    private fun subLabel(primaryCode: String, detailedCode: String): String {
        if (detailedCode == CODE_OTHER) return StringKey.CATEGORY_OTHER.tr()
        val leaf = detailedCode.removePrefix("${primaryCode}_")
        return utils.CategoryUtils.formatCategoryNameLocalized(leaf)
    }

    /** Localized name for [code] at [level], under the parent coded [parentCode].
     *  Which of the two rules above applies is a property of the LEVEL, not of the
     *  column, so a focused scene labels its columns exactly as the full one does. */
    private fun labelFor(level: SankeyLevel, parentCode: String, code: String): String =
        if (level == SankeyLevel.SUB) subLabel(parentCode, code) else primaryLabel(code)

    private fun emptyScene(widthPx: Int, heightPx: Int, isDark: Boolean) = SankeyScene(
        width = widthPx.toDouble(),
        height = heightPx.toDouble(),
        isDark = isDark,
        levels = 0,
        bands = emptyList(),
        emptyText = SankeyText(
            text = StringKey.CHART_NO_DATA_ACCOUNTS.tr(),
            x = (widthPx / 2).toDouble(),
            baselineY = (heightPx / 2).toDouble(),
            fontSize = 12.0,
            colorHex = if (isDark) "#6B7280" else "#9CA3AF",
            anchor = SankeyTextAnchor.MIDDLE,
        ),
    )

    /**
     * The full diagram, income at the root.
     *
     * Its own arity on purpose: Kotlin default arguments are not Swift default
     * arguments, so the ObjC/Swift-visible `layout(flow:width:height:isDark:)`
     * has to exist as a real function for iOS to keep calling it.
     */
    fun layout(flow: BucketFlow, width: Double, height: Double, isDark: Boolean): SankeyScene =
        layout(flow, width, height, isDark, null)

    /**
     * Lays the diagram out, optionally re-rooted on [focus].
     *
     * With [focus] set the scene is the focused band's subtree and nothing else:
     * a bucket becomes the root, its primaries the next column and their
     * sub-categories the one after — so the leaves a portrait phone could never
     * fit now have the whole width to themselves. Percentages are then shares of
     * the focused root, and [SankeyScene.focusLabel] carries its name for the
     * breadcrumb.
     *
     * Focusing a band with nothing to reveal is a **no-op**: the full diagram
     * comes back with [SankeyScene.focus] null, so a caller may ask without
     * knowing in advance whether it will get anywhere. That covers a childless
     * band, income (already the root), a leaf, an unknown code, the merged
     * "Other" and "Remaining" strands — and a band whose focused view would draw
     * a single strand per column, which is a full-width slab that tells the user
     * nothing the band they pressed did not (see [showsAComparison]).
     */
    fun layout(
        flow: BucketFlow,
        width: Double,
        height: Double,
        isDark: Boolean,
        focus: SankeyFocus? = null,
    ): SankeyScene {
        val widthPx = width.toInt()
        val heightPx = height.toInt()
        if (flow.income <= 0.0 || flow.buckets.isEmpty()) return emptyScene(widthPx, heightPx, isDark)

        val density = SankeyLayout.densityFor(widthPx, heightPx)
        val focusedPlan = focus?.let { focusedPlan(flow, it, density, isDark) }
        val plan = focusedPlan ?: unfocusedPlan(flow, density, isDark, heightPx - (PAD * 2))
        return paint(plan, widthPx, heightPx, isDark, density, focus.takeIf { focusedPlan != null })
    }

    /** The full diagram: income at the root, buckets + leftover as its strands. */
    private fun unfocusedPlan(flow: BucketFlow, density: SankeyDensity, isDark: Boolean, plotH: Int): Plan {
        val bucketGap = bucketGapFor(plotH)

        // ---- L1 strands: buckets + leftover, merging a thin Unclassified/leftover tail ----
        val tailGapPx = flow.buckets.size * bucketGap
        fun heightAtFullScale(amount: Double, total: Double) =
            if (total > 0.0) (amount / total) * (plotH - tailGapPx) else 0.0
        val preTotal = flow.buckets.sumOf { it.amount } + flow.leftover
        val unclassifiedAmt = flow.buckets.firstOrNull { it.bucket == BudgetBucket.UNCLASSIFIED }?.amount ?: 0.0
        val leftoverAmt = flow.leftover
        val mergeTail =
            (unclassifiedAmt > 0.0 && heightAtFullScale(unclassifiedAmt, preTotal) < SankeyLayout.MIN_STRAND_PX) ||
                (leftoverAmt > 0.0 && heightAtFullScale(leftoverAmt, preTotal) < SankeyLayout.MIN_STRAND_PX)
        val tailAbsorbedUnclassified = mergeTail && unclassifiedAmt > 0.0

        val perParent = density.strandsPerBucket
        val strands = mutableListOf<Strand>()
        for (b in flow.buckets) {
            if (mergeTail && b.bucket == BudgetBucket.UNCLASSIFIED) continue
            strands += Strand(
                code = b.bucket.name,
                label = b.bucket.displayLabel(),
                amount = b.amount,
                color = bucketColor(b.bucket, isDark),
                interactiveId = if (b.bucket == BudgetBucket.UNCLASSIFIED) "needs-wants-unclassified-strand" else null,
                primaries = if (density.levels >= 3) shownOf(b.primaries, perParent) else null,
            )
        }
        val tailAmt = if (mergeTail) unclassifiedAmt + leftoverAmt else leftoverAmt
        if (tailAmt > 0.0) {
            strands += Strand(
                code = if (tailAbsorbedUnclassified) CODE_OTHER else CODE_REMAINING,
                label = if (tailAbsorbedUnclassified) StringKey.CATEGORY_OTHER.tr() else StringKey.BUDGET_REMAINING.tr(),
                amount = tailAmt,
                color = if (isDark) "#374151" else "#D1D5DB",
                interactiveId = null,
                primaries = null,
            )
        }
        return Plan(
            levels = density.levels,
            columnLevels = listOf(SankeyLevel.INCOME, SankeyLevel.BUCKET, SankeyLevel.PRIMARY, SankeyLevel.SUB),
            rootCode = CODE_INCOME,
            rootLabel = StringKey.DASHBOARD_INCOME.tr(),
            rootAmount = flow.income,
            rootColor = if (isDark) "#4B5563" else "#9CA3AF",
            strands = strands,
        )
    }

    /**
     * Re-roots the diagram on [focus], or returns null when there is nothing to
     * reveal — an unknown code, a level with no children below it (income, a
     * leaf), an empty node, or a subtree so thin that the focused view would be
     * one strand per column ([showsAComparison]) — so the caller can fall back
     * to the full diagram instead of drawing a degenerate one.
     *
     * A focused plan is shallower than the full one by exactly the levels it
     * skips, so [Plan.levels] is capped at the depth actually available: a
     * focused bucket is at most bucket → primaries → subs, and a focused primary
     * at most primary → subs. Otherwise the spans would budget width for a column
     * that has nothing to put in it.
     */
    private fun focusedPlan(flow: BucketFlow, focus: SankeyFocus, density: SankeyDensity, isDark: Boolean): Plan? {
        val perParent = density.strandsPerBucket
        when (focus.level) {
            SankeyLevel.BUCKET -> {
                val bucket = flow.buckets.firstOrNull { it.bucket.name == focus.categoryCode } ?: return null
                if (bucket.amount <= 0.0 || bucket.primaries.isEmpty()) return null
                val color = bucketColor(bucket.bucket, isDark)
                val shown = shownOf(bucket.primaries, perParent)
                val levels = density.levels
                    .coerceAtMost(if (shown.any { it.children.isNotEmpty() }) 3 else 2)
                val plan = Plan(
                    levels = levels,
                    columnLevels = listOf(SankeyLevel.BUCKET, SankeyLevel.PRIMARY, SankeyLevel.SUB, SankeyLevel.SUB),
                    rootCode = bucket.bucket.name,
                    rootLabel = bucket.bucket.displayLabel(),
                    rootAmount = bucket.amount,
                    rootColor = color,
                    strands = shown.map { cat ->
                        Strand(
                            code = cat.label,
                            label = primaryLabel(cat.label),
                            amount = cat.amount,
                            color = color,
                            interactiveId = null,
                            primaries = if (levels >= 3) shownOf(cat.children, perParent) else null,
                        )
                    },
                )
                return plan.takeIf { it.showsAComparison() }
            }
            SankeyLevel.PRIMARY -> {
                // A primary can sit under more than one bucket (a split category),
                // so the code alone does not name a band. When the focus carries
                // its parent we resolve inside THAT bucket — and nowhere else, so
                // a stale parent is a no-op rather than the wrong subtree. Without
                // one, the first match wins: the same copy the full diagram draws
                // first, which is what every caller used to get.
                val candidates =
                    if (focus.parentBucket != null) flow.buckets.filter { it.bucket.name == focus.parentBucket }
                    else flow.buckets
                val hit = candidates.firstNotNullOfOrNull { bucket ->
                    bucket.primaries.firstOrNull { it.label == focus.categoryCode }?.let { bucket to it }
                } ?: return null
                val (bucket, primary) = hit
                if (primary.amount <= 0.0 || primary.children.isEmpty()) return null
                val color = bucketColor(bucket.bucket, isDark)
                val plan = Plan(
                    levels = density.levels.coerceAtMost(2),
                    columnLevels = listOf(SankeyLevel.PRIMARY, SankeyLevel.SUB, SankeyLevel.SUB, SankeyLevel.SUB),
                    rootCode = primary.label,
                    rootLabel = primaryLabel(primary.label),
                    rootAmount = primary.amount,
                    rootColor = color,
                    strands = shownOf(primary.children, perParent).map { sub ->
                        Strand(
                            code = sub.label,
                            label = subLabel(primary.label, sub.label),
                            amount = sub.amount,
                            color = color,
                            interactiveId = null,
                            primaries = null,
                        )
                    },
                )
                return plan.takeIf { it.showsAComparison() }
            }
            // INCOME is already the root; SUB is a leaf. Nothing to drill into.
            else -> return null
        }
    }

    /**
     * True when some column of this focused plan holds more than one band —
     * i.e. there is actually a split to look at.
     *
     * A plan that draws ONE band per column is a chain of full-width slabs
     * (General Merchandise $25 → Discount Store $25): it restates the band the
     * user pressed at 100% and reads as a bug. Such a focus is treated exactly
     * like the childless one — [focusedPlan] returns null, [layout] hands back
     * the unfocused scene with `focus == null`, and the caller shows its detail
     * bubble instead of zooming.
     *
     * Judged AFTER the roll-up and after [Plan.levels] is capped, because only
     * the painted counts answer "what does the user SEE":
     *  - [shownOf] can fold a long tail into "Other", so N raw children may be
     *    drawn as `perParent` + Other — still two strands, still worth focusing;
     *    and conversely a lone "Other" strand is one slab however many rows fed
     *    it. Raw child counts would get both cases wrong.
     *  - the level cap can hide a column the raw tree has: a bucket whose only
     *    primary does split renders as one slab at 2 levels but as a real
     *    breakdown at 3, so the same subtree is degenerate at one size and not
     *    at another. Only the plan knows which.
     *
     * Strands worth zero are excluded because [paint] skips them too.
     */
    private fun Plan.showsAComparison(): Boolean {
        val drawn = strands.filter { it.amount > 0.0 }
        if (drawn.size > 1) return true
        if (levels < 3) return false
        // A single strand still earns the drill-down if the column below it
        // splits — the focused view is then a breakdown, not a slab.
        return drawn.any { s -> (s.primaries ?: emptyList()).count { it.amount > 0.0 } > 1 }
    }

    /**
     * Turns a [Plan] into pixels. The only thing the focused scenes change is
     * which [SankeyLevel] each column carries and what the root is, so the scale,
     * the packing and the conservation rules are written once here.
     */
    private fun paint(
        plan: Plan,
        widthPx: Int,
        heightPx: Int,
        isDark: Boolean,
        density: SankeyDensity,
        focus: SankeyFocus?,
    ): SankeyScene {
        val labelColor = if (isDark) "#ABABB8" else "#555560"
        val plotH = heightPx - (PAD * 2)
        val gap = gapFor(plotH)
        val bucketGap = bucketGapFor(plotH)
        // Per-level left-edge x positions. Spans are unequal: the income→buckets
        // "blob" compresses first, the labelled spans hold their width longer
        // (see SankeyLayout.spanWidths), so narrowing keeps the leaves.
        val spans = SankeyLayout.spanWidths(plan.levels, SankeyLayout.usableSpanWidth(widthPx))
        val levelX = DoubleArray(plan.levels)
        levelX[0] = PAD.toDouble()
        for (i in 1 until plan.levels) levelX[i] = levelX[i - 1] + spans[i - 1]

        val strands = plan.strands
        val perParent = density.strandsPerBucket
        val l1Level = plan.columnLevels[1]
        val l2Level = plan.columnLevels[2]
        val l3Level = plan.columnLevels[3]
        val totalFlow = strands.sumOf { it.amount }.takeIf { it > 0.0 }
            ?: return emptyScene(widthPx, heightPx, isDark)

        // ---- pick drilled primaries (L4) up front so the scale can budget their gaps ----
        val allPrimaries = strands.flatMap { s -> (s.primaries ?: emptyList()).map { s to it } }
        val splitLabels = allPrimaries.map { it.second.label }
            .groupingBy { it }.eachCount().filterValues { it > 1 }.keys
        // Drill more primaries when there's vertical room. A primary with a REAL
        // breakdown — a cross-bucket split, or ≥2 sub-categories — earns a column
        // ahead of a single-child primary (Government→Tax) whose only sub just
        // echoes its own name, so General Merchandise's Gifts/Online split wins a
        // slot instead of being spent on a redundant one.
        val maxDrilled = (plotH / 70).coerceIn(3, 8)
        val drilledPick = if (plan.levels >= 4) {
            allPrimaries
                .filter { it.second.children.isNotEmpty() && it.second.label != CODE_OTHER }
                .sortedWith(
                    compareByDescending<Pair<Strand, ShownCat>> { it.second.label in splitLabels }
                        .thenByDescending { it.second.children.size >= 2 }
                        .thenByDescending { it.second.amount },
                )
                .take(maxDrilled)
        } else emptyList()
        // Key a drilled pick by (strand identity, label) so the L2 pass can recognise it.
        val drilledKeys = drilledPick.map { it.first to it.second.label }.toSet()
        val subsFor = drilledPick.associate { (s, cat) -> (s to cat.label) to shownOf(cat.children, perParent) }

        // ---- one value→px scale, sized so the busiest column fits ----
        // Each column is described by the amounts actually drawn: a non-positive
        // one is skipped below, so it neither claims a floor nor earns a gap.
        val columns = ArrayList<Column>()
        val drawnStrands = strands.filter { it.amount > 0.0 }
        columns += Column(
            drawnStrands.map { it.amount },
            ((drawnStrands.size - 1).coerceAtLeast(0) * bucketGap).toDouble(),
        )
        if (plan.levels >= 3) {
            val groups = strands.filter { !it.primaries.isNullOrEmpty() }
            val drawn = groups.map { g -> g.primaries!!.filter { it.amount > 0.0 } }
            val within = drawn.sumOf { (it.size - 1).coerceAtLeast(0) } * gap
            val between = (groups.size - 1).coerceAtLeast(0) * bucketGap
            val vals = drawn.flatten().map { it.amount }
            if (vals.isNotEmpty()) columns += Column(vals, (within + between).toDouble())
        }
        if (drilledPick.isNotEmpty()) {
            val drawn = drilledPick.map { p ->
                subsFor[p.first to p.second.label]!!.filter { it.amount > 0.0 }
            }
            val within = drawn.sumOf { (it.size - 1).coerceAtLeast(0) } * gap
            val between = (drilledPick.size - 1).coerceAtLeast(0) * bucketGap
            val vals = drawn.flatten().map { it.amount }
            if (vals.isNotEmpty()) columns += Column(vals, (within + between).toDouble())
        }

        val nodeFloor = floorFor(columns, plotH.toDouble())
        val scale = fitScale(columns, plotH.toDouble(), nodeFloor)

        val bands = ArrayList<SankeyBand>()

        // ---- L0 root: sum of the strands it feeds (no gaps), centred against L1 ----
        val incomeColor = plan.rootColor
        val incomeH = totalFlow * scale
        val incomeTop = PAD + (plotH - incomeH) / 2.0
        bands += SankeyBand(
            level = plan.columnLevels[0],
            categoryCode = plan.rootCode,
            label = plan.rootLabel,
            amount = plan.rootAmount,
            amountText = money(plan.rootAmount),
            parentLabel = null,
            parentCode = null,
            parentAmount = null,
            percentOfParent = null,
            colorHex = incomeColor,
            interactiveId = null,
            ribbon = null,
            node = SankeyNode(PAD.toDouble(), incomeTop, NODE_W.toDouble(), incomeH, 3.0, incomeColor, 1.0),
            text = null,
        )

        // ---- L1 strands ----
        val bucketX = levelX[1].toInt()
        val l1ColH = drawnStrands.sumOf { nodeH(it.amount, scale, nodeFloor) } +
            (drawnStrands.size - 1).coerceAtLeast(0) * bucketGap
        var l1Y = PAD + (plotH - l1ColH) / 2.0
        var incomeCursor = incomeTop
        val placedStrands = ArrayList<Placed>()
        for (s in drawnStrands) {
            // The bar drawn and the flow it carries part company once the floor
            // bites: the ribbon keeps the honest thickness and enters the stub at
            // the stub's centre.
            val t = s.amount * scale
            val h = nodeH(s.amount, scale, nodeFloor)
            val ribbonTop = l1Y + (h - t) / 2
            var text: SankeyText? = null
            if (h >= labelMinFor(l1Level)) {
                val pct = ((s.amount / plan.rootAmount) * 100).roundToInt()
                val fitted = fitLabel("${s.label} $pct%", 13, labelBudget(bucketX, widthPx))
                if (fitted.isNotEmpty()) {
                    text = SankeyText(
                        text = fitted,
                        x = (bucketX + NODE_W + LABEL_PAD).toDouble(),
                        baselineY = l1Y + h / 2 + 4,
                        fontSize = 13.0,
                        colorHex = labelColor,
                        anchor = SankeyTextAnchor.START,
                    )
                }
            }
            bands += SankeyBand(
                level = l1Level,
                categoryCode = s.code,
                label = s.label,
                amount = s.amount,
                amountText = money(s.amount),
                parentLabel = plan.rootLabel,
                parentCode = plan.rootCode,
                parentAmount = plan.rootAmount,
                percentOfParent = pctOf(s.amount, plan.rootAmount),
                colorHex = s.color,
                interactiveId = s.interactiveId,
                ribbon = ribbon(PAD + NODE_W, incomeCursor, bucketX, ribbonTop, t, s.color),
                node = SankeyNode(bucketX.toDouble(), l1Y, NODE_W.toDouble(), h, 3.0, s.color, 1.0),
                text = text,
            )
            placedStrands += Placed(s, l1Y, h, t)
            incomeCursor += t
            l1Y += h + bucketGap
        }

        // ---- L2 (flush at its L1 parent, packed with gaps in this column) ----
        val drilledPlacements = ArrayList<Drilled>()
        if (plan.levels >= 3) {
            val catX = levelX[2].toInt()
            val groups = placedStrands.filter { !it.strand.primaries.isNullOrEmpty() }
            val drawn = groups.map { g -> g.strand.primaries!!.filter { it.amount > 0.0 } }
            val within = drawn.sumOf { (it.size - 1).coerceAtLeast(0) } * gap
            val between = (groups.size - 1).coerceAtLeast(0) * bucketGap
            val colH = drawn.flatten().sumOf { nodeH(it.amount, scale, nodeFloor) } + within + between
            var y = PAD + (plotH - colH) / 2.0
            for ((gi, g) in groups.withIndex()) {
                val s = g.strand
                // Flush from where the parent's own flow enters its stub, so the
                // children still sum to exactly the parent's flow even when that
                // parent's bar was floored taller than the flow it carries.
                var src = g.y + (g.nodeH - g.thickness) / 2
                for (cat in drawn[gi]) {
                    val t = cat.amount * scale
                    val h = nodeH(cat.amount, scale, nodeFloor)
                    val ribbonTop = y + (h - t) / 2
                    val label = labelFor(l2Level, s.code, cat.label)
                    var text: SankeyText? = null
                    if (h >= labelMinFor(l2Level)) {
                        val fitted = fitLabel(label, 12, labelBudget(catX, widthPx))
                        if (fitted.isNotEmpty()) {
                            text = SankeyText(
                                text = fitted,
                                x = (catX + NODE_W + LABEL_PAD).toDouble(),
                                baselineY = y + h / 2 + 4,
                                fontSize = 12.0,
                                colorHex = labelColor,
                                anchor = SankeyTextAnchor.START,
                            )
                        }
                    }
                    bands += SankeyBand(
                        level = l2Level,
                        categoryCode = cat.label,
                        label = label,
                        amount = cat.amount,
                        amountText = money(cat.amount),
                        parentLabel = s.label,
                        parentCode = s.code,
                        parentAmount = s.amount,
                        percentOfParent = pctOf(cat.amount, s.amount),
                        colorHex = s.color,
                        interactiveId = null,
                        ribbon = ribbon(bucketX + NODE_W, src, catX, ribbonTop, t, s.color),
                        node = SankeyNode(catX.toDouble(), y, NODE_W.toDouble(), h, 2.0, s.color, 0.85),
                        text = text,
                    )
                    if ((s to cat.label) in drilledKeys) {
                        drilledPlacements += Drilled(cat.label, label, s.color, ribbonTop, cat.amount, subsFor[s to cat.label]!!)
                    }
                    src += t
                    y += h + gap
                }
                y += bucketGap - gap // upgrade the last within-gap to a between-bucket gap
            }
        }

        // ---- L3 leaves (centred column, flush at their L2 parent) ----
        if (drilledPlacements.isNotEmpty()) {
            val catX = levelX[2].toInt()
            val subX = levelX[3].toInt()
            val drawn = drilledPlacements.map { d -> d.subs.filter { it.amount > 0.0 } }
            val within = drawn.sumOf { (it.size - 1).coerceAtLeast(0) } * gap
            val between = (drilledPlacements.size - 1).coerceAtLeast(0) * bucketGap
            val colH = drawn.flatten().sumOf { nodeH(it.amount, scale, nodeFloor) } + within + between
            var y = PAD + (plotH - colH) / 2.0
            for ((di, d) in drilledPlacements.withIndex()) {
                var src = d.srcY
                for (sub in drawn[di]) {
                    val t = sub.amount * scale
                    val h = nodeH(sub.amount, scale, nodeFloor)
                    val ribbonTop = y + (h - t) / 2
                    val label = labelFor(l3Level, d.code, sub.label)
                    var text: SankeyText? = null
                    if (h >= labelMinFor(l3Level)) {
                        val fitted = fitLabel(label, 11, labelBudget(subX, widthPx))
                        if (fitted.isNotEmpty()) {
                            text = SankeyText(
                                text = fitted,
                                x = (subX + NODE_W + LABEL_PAD).toDouble(),
                                baselineY = y + h / 2 + 4,
                                fontSize = 11.0,
                                colorHex = labelColor,
                                anchor = SankeyTextAnchor.START,
                            )
                        }
                    }
                    bands += SankeyBand(
                        level = l3Level,
                        categoryCode = sub.label,
                        label = label,
                        amount = sub.amount,
                        amountText = money(sub.amount),
                        parentLabel = d.label,
                        parentCode = d.code,
                        parentAmount = d.amount,
                        percentOfParent = pctOf(sub.amount, d.amount),
                        colorHex = d.color,
                        interactiveId = null,
                        ribbon = ribbon(catX + NODE_W, src, subX, ribbonTop, t, d.color),
                        node = SankeyNode(subX.toDouble(), y, NODE_W.toDouble(), h, 2.0, d.color, 0.7),
                        text = text,
                    )
                    src += t
                    y += h + gap
                }
                y += bucketGap - gap
            }
        }

        return SankeyScene(
            width = widthPx.toDouble(),
            height = heightPx.toDouble(),
            isDark = isDark,
            levels = plan.levels,
            bands = bands,
            emptyText = null,
            focus = focus,
            focusLabel = focus?.let { plan.rootLabel },
            focusAmount = focus?.let { plan.rootAmount },
            focusAmountText = focus?.let { money(plan.rootAmount) },
        )
    }

    /** Whole-percent share of [parent], or null when there is no meaningful parent. */
    private fun pctOf(amount: Double, parent: Double): Int? =
        if (parent > 0.0) ((amount / parent) * 100).roundToInt() else null

    /** Thinnest band still worth an inline label at [level]. Leaves get a lower
     *  bar than the columns above them — see [LEAF_LABEL_MIN_PX]. */
    private fun labelMinFor(level: SankeyLevel): Int =
        if (level == SankeyLevel.SUB) LEAF_LABEL_MIN_PX else LABEL_MIN_PX
}
